import React, { useEffect, useState } from 'react';
import { useOracles, ConsensusData } from '../hooks/useOracles';

interface OracleConsensusTableProps {
  marketId: number;
}

/**
 * OracleConsensusTable
 *
 * Displays per-oracle votes and the current consensus state for a specific
 * market. Polls the PredictionMarketMultiOracle contract for live data.
 */
export default function OracleConsensusTable({ marketId }: OracleConsensusTableProps) {
  const { fetchConsensus, isConfigured } = useOracles();
  const [consensus, setConsensus] = useState<ConsensusData | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!isConfigured) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchConsensus(marketId);
        if (!cancelled) setConsensus(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load consensus data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [marketId, fetchConsensus, isConfigured]);

  if (!isConfigured) {
    return (
      <div className="empty-state">
        Multi-oracle contract not configured.
        Set <code>REACT_APP_MULTI_ORACLE_ADDRESS</code> to enable consensus view.
      </div>
    );
  }

  if (loading) return <div className="loading-state">Loading consensus data…</div>;
  if (error)   return <div className="error-state">⚠️ {error}</div>;
  if (!consensus) return <div className="empty-state">No consensus data available.</div>;

  const yesPercent = consensus.yesVotes + consensus.noVotes > 0
    ? Math.round((consensus.yesVotes / (consensus.yesVotes + consensus.noVotes)) * 100)
    : 0;

  return (
    <div className="oracle-consensus-table">
      {/* Summary row */}
      <div className="consensus-summary">
        <div className="consensus-votes">
          <span className="vote-yes">YES: {consensus.yesVotes}</span>
          <span className="vote-separator">·</span>
          <span className="vote-no">NO: {consensus.noVotes}</span>
          <span className="vote-separator">·</span>
          <span>Quorum: {consensus.quorum}</span>
        </div>
        <div className="consensus-bar" title={`${yesPercent}% YES`}>
          <div
            className="consensus-bar-yes"
            style={{ width: `${yesPercent}%` }}
          />
        </div>
        <div className="consensus-status">
          {consensus.resolved ? (
            <span className={`badge ${consensus.outcome ? 'badge-yes' : 'badge-no'}`}>
              Resolved: {consensus.outcome ? 'YES ✓' : 'NO ✗'}
              {consensus.disputed && ' (Disputed)'}
            </span>
          ) : (
            <span className="badge badge-open">
              Pending ({consensus.yesVotes + consensus.noVotes}/{consensus.quorum} votes)
            </span>
          )}
        </div>
      </div>

      {/* Per-oracle vote table */}
      {consensus.oracleVotes.length > 0 && (
        <table className="consensus-votes-table">
          <thead>
            <tr>
              <th>Oracle</th>
              <th>Vote</th>
              <th>Price</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {consensus.oracleVotes.map(v => (
              <tr key={v.oracle}>
                <td className="oracle-addr">
                  {v.oracle.slice(0, 8)}…{v.oracle.slice(-6)}
                </td>
                <td>
                  <span className={`badge ${v.outcome ? 'badge-yes' : 'badge-no'}`}>
                    {v.outcome ? 'YES' : 'NO'}
                  </span>
                </td>
                <td>{v.price !== '0' ? v.price : '—'}</td>
                <td>
                  {v.timestamp > 0
                    ? new Date(v.timestamp * 1000).toLocaleString()
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
